import { Organizer } from '@/types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchOrganizerFollowStatusExpansion } from './fetchers/fetchOrganizerFollowStatusExpansion';

export type FollowOrganizerContextType = {
  isFollowing: boolean;
  numFollowers: number;
  loading: boolean;
  updateFollowStatus: (isFollowing: boolean) => void;
  updateNumFollowers: (numFollowers: number) => void;
};

export const FollowOrganizerContext = createContext<FollowOrganizerContextType | null>(null);

export const OrganizerFollowStatusProvider: React.FC<{
  children: React.ReactNode;
  organizer: Organizer | null | undefined;
}> = ({ children, organizer }) => {
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [numFollowers, setNumFollowers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!organizer?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchOrganizerFollowStatusExpansion(organizer.id);
        setIsFollowing(data.followedByYou);
        setNumFollowers(data.numFollowers);
      } catch (error) {
        console.error('Failed to fetch follow status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [organizer?.id]);

  const updateFollowStatus = (isFollowing: boolean) => {
    setIsFollowing(isFollowing);
  };

  const updateNumFollowers = (numFollowers: number) => {
    setNumFollowers(numFollowers);
  };

  const contextValue: FollowOrganizerContextType = {
    isFollowing,
    numFollowers,
    loading,
    updateFollowStatus,
    updateNumFollowers,
  };

  return <FollowOrganizerContext.Provider value={contextValue}>{children}</FollowOrganizerContext.Provider>;
};

export const useOrganizerFollowStatus = (): FollowOrganizerContextType => {
  const context = useContext(FollowOrganizerContext);
  if (!context) {
    throw new Error('useOrganizerFollowStatus must be used within an OrganizerFollowStatusProvider');
  }
  return context;
};
